import {
	createSlice,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'
import { envAwarePost } from '../util/util'
import {addZero} from '../util/datetime'
import moment from 'moment'
import {filterTrainingSessions} from './events'

export const registerForGroupSession = createAsyncThunk(
	'registerForGroupSession',
	async payload => await envAwarePost(
		'api/scheduler/',
		{action:'registerForGroupSession',...payload}
	)
)

export const unregisterFromGroupSession = createAsyncThunk(
	'unregisterFromGroupSession',
	async payload => await envAwarePost(
		'api/scheduler/',
		{action:'unregisterFromGroupSession',...payload}
	)
)

export const getAllEventsInRange = createAsyncThunk(
	"getAllEventsInRange",
	async (payload) => envAwarePost("api/scheduler/api.php", payload)
);

export const createCalendarEvent = createAsyncThunk(
	"createCalendarEvent",
	async (payload) => envAwarePost("api/scheduler/api.php", payload)
);

export const editCalendarEvent = createAsyncThunk(
	"editCalendarEvent",
	async (payload) => envAwarePost("api/scheduler/api.php", payload)
);

export const editEndDate = createAsyncThunk(
	"editEndDate",
	async (payload) => envAwarePost("api/scheduler/api.php", payload)
);

export const editCalendarDate = createAsyncThunk(
	"editCalendarDate",
	async (payload) => envAwarePost("api/scheduler/api.php", payload)
);

export const deleteCalendarEvent = createAsyncThunk(
	"deleteCalendarEvent",
	async (payload,{dispatch}) => {
		//dispatch filtering to remove workouts associated
		//with the calendar event
		dispatch(filterTrainingSessions(payload.event_id))
		return envAwarePost("api/scheduler/api.php", payload)
	}
);

/* --------- Availability Thunks --------- */

export const getWeekdayAvailabilities = createAsyncThunk(
	'getWeekdayAvailabilities',
	async ()=> await envAwarePost(
		'api/scheduler/',
		{action:'getWeekdayAvailabilities'}
	)
)

export const editWeekdayAvailability = createAsyncThunk(
	'editWeekdayAvailability',
	async payload => await envAwarePost(
		'api/scheduler/',
		{action:'editWeekdayAvailability',...payload}
	)
)

export const createWeekdayAvailabilityRange = createAsyncThunk(
	'createWeekdayAvailabilityRange',
	async ({weekday_availability_id},{getState}) => {
		//get latest availability range and create a 1hr range
		//that is 1hr after the end of the latest range. If there
		//are no ranges set to 8am-9am
		const state = getState()
		//recall ranges are normalized so this is an array of ids
		const {ranges} = state.scheduler.weekdayAvailabilities.entities[
			weekday_availability_id
		]
		//get the lateset range using the last id
		const latestRange = state.scheduler.weekdayAvailabilityRanges.entities[
			ranges[ranges.length-1]
		]
		const addHour = time => {
			const segments = time.split(':').map(s=>parseInt(s))
			segments[0] += 1
			return segments.map(addZero).join(':')
		}

		const start_time = (
			ranges.length > 0 
			? addHour(latestRange.end_time)
			: '08:00:00'
		)
		const end_time = addHour(start_time)

		const res = await envAwarePost(
			'api/scheduler/',
			{
				action:'createWeekdayAvailabilityRange',
				weekday_availability_id,
				start_time,
				end_time
			}
		)
		return res
	}
)

export const editWeekdayAvailabilityRange = createAsyncThunk(
	'editWeekdayAvailabilityRange',
	async payload => await envAwarePost(
		'api/scheduler/',
		{action:'editWeekdayAvailabilityRange',...payload}
	)
)

export const removeWeekdayAvailabilityRange = createAsyncThunk(
	'removeWeekdayAvailabilityRange',
	async id => await envAwarePost(
		'api/scheduler/',
		{action:'removeWeekdayAvailabilityRange',id}
	)
)

export const copyWeekdayAvailabilityRanges = createAsyncThunk(
	'copyWeekdayAvailabilityRanges',
	async payload => await envAwarePost(
		'api/scheduler/',
		{action:'copyWeekdayAvailabilityRanges',...payload}
	)
)

// formats event data for the calendar component

const weekdayAvailabilityAdapter = createEntityAdapter()
const weekdayAvailabilityRangeAdapter = createEntityAdapter()

//this reducer is used twice so it was put here to keep code DRY
const weekdayAvailabilitiesReducer = (state,action) => {
	state.weekdayAvailabilities.status = 'idle'
	//this api call returns nested data but this reducer
	//normalizes it
	const availabilities = action.payload.data.weekday_availabilities
	//extract all ranges
	const weekdayAvailabilityRanges = availabilities.reduce(
		(ranges,availabilitiy)=>[...ranges,...availabilitiy.ranges]
	,[])
	//normalize availabilites (ie denest the ranges)
	const availabilitiesNormalized = availabilities.map(a=>{
		a.ranges = a.ranges.map( r => r.id )
		return a
	})
	//insert normalized data into the state
	weekdayAvailabilityAdapter.upsertMany(
		state.weekdayAvailabilities,
		availabilitiesNormalized
	)

	weekdayAvailabilityRangeAdapter.upsertMany(
		state.weekdayAvailabilityRanges,
		weekdayAvailabilityRanges
	)
}

const schedulerSlice = createSlice({
	name: 'schedulerSlice',
	initialState: {
		eventData: [],
		weekdayAvailabilities:weekdayAvailabilityAdapter.getInitialState({
			show:false,
			status:'init'
		}),
		weekdayAvailabilityRanges:weekdayAvailabilityRangeAdapter.getInitialState(),
	},
	reducers: {
		toggleWeekdayAvailabilities:(state,action)=>{
			state.weekdayAvailabilities.show = action.payload
		}
	},
	extraReducers: {
		[getAllEventsInRange.fulfilled]: (state, action) => {
            state.eventData = action.payload.data
		},
		[getWeekdayAvailabilities.pending] : (state,action) => {
			state.weekdayAvailabilities.status = 'pending'
		},
		
		[getWeekdayAvailabilities.fulfilled] : weekdayAvailabilitiesReducer,

		[copyWeekdayAvailabilityRanges.fulfilled] : weekdayAvailabilitiesReducer,

		[editWeekdayAvailability.pending] : (state,action) => {
			const {id,update:changes} = action.meta.arg
			weekdayAvailabilityAdapter.updateOne(
				state.weekdayAvailabilities,
				{id,changes}
			)
		},
		[createWeekdayAvailabilityRange.fulfilled] : (state,action)=>{
			const range = action.payload.data.weekday_availability_range
			//push id to normalized weekday availability
			state.weekdayAvailabilities.entities[range.weekday_availability_id].ranges.push(range.id)
			//upsert the range
			weekdayAvailabilityRangeAdapter.upsertOne(
				state.weekdayAvailabilityRanges,
				range
			)
		},
		[editWeekdayAvailabilityRange.pending] : (state,action) => {
			const {id,update:changes} = action.meta.arg
			weekdayAvailabilityRangeAdapter.updateOne(
				state.weekdayAvailabilityRanges,
				{id,changes}
			)
		},
		[removeWeekdayAvailabilityRange.pending] : (state,action) => {
			const id = action.meta.arg
			//get the weekday availability range 
			const weekdayAvailabilityRange = state.weekdayAvailabilityRanges.entities[id]
			//get the id of the containing weekday availability
			const weekdayAvailabilityId = weekdayAvailabilityRange.weekday_availability_id
			//get the container weekday availabiliyt
			const weekdayAvailability = state.weekdayAvailabilities.entities[weekdayAvailabilityId]
			//filter this id from the list of ranges
			weekdayAvailability.ranges = weekdayAvailability.ranges.filter(rangeId=>rangeId!==id)
			//remove this range from the state
			weekdayAvailabilityRangeAdapter.removeOne(state.weekdayAvailabilityRanges,id)
		}
	}
})

export const { 
	toggleWeekdayAvailabilities
} = schedulerSlice.actions

export const {
	selectAll: selectAllWeekdayAvailabilities,
	selectById: selectWeekdayAvailabilityById,
	selectIds: selectWeekdayAvailabilityIds
} = weekdayAvailabilityAdapter.getSelectors( state => state.scheduler.weekdayAvailabilities )

export const {
	selectAll: selectAllWeekdayAvailabilityRanges,
	selectById: selectWeekdayAvailabilityRangeById,
	selectIds: selectWeekdayAvailabilityRangeIds
} = weekdayAvailabilityRangeAdapter.getSelectors( state => state.scheduler.weekdayAvailabilityRanges )

export default schedulerSlice.reducer